import React, { useEffect, useCallback, useState } from "react";
import { FieldArrayRenderProps } from "react-final-form-arrays";
import {
  Record,
  Datagrid,
  Pagination,
  ResourceContextProvider,
  Identifier,
  useListContext,
  useRecordContext,
} from "react-admin";
import { Card } from "@material-ui/core";
import { difference } from "lodash";
import { CompactListBase, Sort } from "../../form/CompactListBase";
import { SelectAllHandler } from "./SelectAllHandler";

interface Props extends Partial<FieldArrayRenderProps<any, HTMLElement>> {
  reference: string;
  children: React.ReactNode;
  record?: Record;
  source?: string;
  sort?: Sort;
  filter?: any;
  itemIdField?: string;
  renderSelectedExtra?: (record: Record) => React.ReactNode;
}

interface SelectedItemsHandlerProps extends Partial<Props> {
  handleSelectedItems: (ids: Array<Identifier>) => void;
}

interface SelectedExtraFieldProps {
  render: (record: Record) => React.ReactNode;
}

const SelectedExtraField = ({ render }: SelectedExtraFieldProps) => {
  const { selectedIds } = useListContext();
  const record = useRecordContext<Record>();

  if (!record) {
    return null;
  }

  const id = record.id as Identifier;
  if (!selectedIds.includes(id)) {
    return null;
  }

  return <>{render(record)}</>;
};

const SelectedItemsHandler = ({
  handleSelectedItems,
  fields,
  itemIdField,
}: SelectedItemsHandlerProps) => {
  const [initialized, setInitialized] = useState<boolean>(false);
  const { selectedIds, onSelect } = useListContext();
  useEffect(() => {
    if (!initialized) {
      return;
    }

    handleSelectedItems(selectedIds);
  }, [selectedIds, handleSelectedItems, initialized]);

  useEffect(() => {
    if (initialized) {
      return;
    }

    const fieldValue = fields ? (fields).value : undefined;
    if (!Array.isArray(fieldValue) || fieldValue.length === 0 || !itemIdField) {
      onSelect([]);
      setInitialized(true);
      return;
    }

    const first = fieldValue[0];
    const hasObjectsWithIdField =
      first && typeof first === "object" && itemIdField in first;

    const initialIds: Identifier[] = hasObjectsWithIdField
      ? fieldValue
        .map((item: any) => item && item[itemIdField])
        .filter((x: Identifier | undefined) => x != null)
      : (fieldValue as Identifier[]);

    onSelect(initialIds);
    setInitialized(true);
  }, [fields, itemIdField, onSelect, initialized]);

  return null;
};

export const VehiclePeriodListIterator = ({
  fields,
  reference,
  record,
  source,
  children,
  sort = { field: "id", order: "asc" },
  filter,
  itemIdField,
  renderSelectedExtra,
}: Props) => {
  const handleSelectedItems = useCallback(
    (ids: Array<Identifier>) => {
      if (!fields) {
        console.warn("fields was undefined in VehiclePeriodListIterator.handleSelectedItems");
        return;
      }

      const currentValues: any[] = fields.value ?? [];

      const idField = itemIdField;
      const currentIds: Identifier[] = idField
        ? currentValues.map((item) => item && item[idField])
        : currentValues;

      const toAdd = difference<Identifier>(ids, currentIds);
      const toRemove = difference<Identifier>(currentIds, ids);

      const indicesToRemove: number[] = [];
      for (const id of toRemove) {
        const index = currentValues.findIndex((item) =>
          idField ? item && item[idField] === id : item === id
        );
        if (index === -1) {
          console.warn(
            `Tried to remove the item '${id}', but it was not found in fields.value`,
            { currentValues }
          );
          continue;
        }

        indicesToRemove.push(index);
      }

      indicesToRemove.sort((a, b) => b - a);
      for (const index of indicesToRemove) {
        fields.remove(index);
      }

      for (const item of toAdd) {
        if (idField) {
          fields.push({ [idField]: item } as any);
        } else {
          fields.push(item);
        }
      }
    },
    [fields, itemIdField]
  );

  return (
    <ResourceContextProvider value={reference}>
      <CompactListBase
        resource={reference}
        basePath={`/${reference}`}
        perPage={25}
        sort={sort}
        filter={filter}
      >
        <SelectedItemsHandler
          handleSelectedItems={handleSelectedItems}
          fields={fields}
          record={record}
          source={source}
          itemIdField={itemIdField}
        />
        <Card>
          <SelectAllHandler resource={reference} filter={filter} />
          <Datagrid hasBulkActions>
            {children}
            {renderSelectedExtra && (
              <SelectedExtraField render={renderSelectedExtra} />
            )}
          </Datagrid>
          <Pagination rowsPerPageOptions={[25, 50, 100]} />
        </Card>
      </CompactListBase>
    </ResourceContextProvider>
  );
}

